import { useCallback, useEffect, useRef, useState } from 'react';

const ACTIVE_INTERVAL_MS = 2000;
const HIDDEN_INTERVAL_MS = 10000;
const BACKOFF_CAP_MS = 30000;
/**
 * How many consecutive 404s are tolerated before the resource is called missing. A caller that
 * polls by an id it was just handed can legitimately be ahead of the row: the deploy dialog
 * has its deployment id from its own 202 before graph-service has written the saga run, and a
 * sphere-scoped grant can take a moment to be visible. Reporting "this deployment could not be
 * found" on the first poll told those users their deployment did not exist.
 */
const NOT_FOUND_GRACE_ATTEMPTS = 3;

export type PolledResourceDenialReason = 'forbidden' | 'not_found';

export interface UsePolledResourceOptions<T> {
  /** Polling runs only while `true`. Flip to `false` to pause (e.g. dialog closed). */
  active: boolean;
  /** A value for which polling stops for good — the thing being watched has finished. */
  isTerminal: (value: T) => boolean;
}

export interface UsePolledResourceResult<T> {
  /** The last successful fetch's value — kept across failed polls so a transient failure does not blank the surface. */
  data: T | null;
  loading: boolean;
  /** Non-null while consecutive fetch failures (other than 403/404) are ongoing. */
  error: Error | null;
  /** How many fetches in a row have failed (other than 403/404); 0 after any success. */
  consecutiveFailures: number;
  /** Set once a 403 or 404 is seen; polling has stopped permanently. */
  deniedReason: PolledResourceDenialReason | null;
  refetch: () => void;
}

function httpStatusOf(err: unknown): number | undefined {
  return (err as { response?: { status?: number } } | undefined)?.response?.status;
}

/**
 * Polls `fetcher` on a 2 s cadence (10 s while the tab is hidden, with an immediate fetch when the
 * tab comes back), stopping permanently at a value `isTerminal` accepts, on a 403, and on a 404
 * that outlasts `NOT_FOUND_GRACE_ATTEMPTS` (or follows a value). Any other fetch failure backs off
 * exponentially to a 30 s cap and is exposed via `error` / `consecutiveFailures` until the next
 * successful fetch, with the last good `data` kept.
 *
 * The one polling engine for long-running server work a user waits on — `useSagaProgress` is this
 * hook over a saga-progress fetcher, and the PWA's sign-up wait is this hook over
 * `provisioning-status`.
 */
export function usePolledResource<T>(
  fetcher: () => Promise<T>,
  { active, isTerminal }: UsePolledResourceOptions<T>
): UsePolledResourceResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);
  const [deniedReason, setDeniedReason] = useState<PolledResourceDenialReason | null>(null);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const isTerminalRef = useRef(isTerminal);
  isTerminalRef.current = isTerminal;

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const consecutiveFailuresRef = useRef(0);
  const notFoundAttemptsRef = useRef(0);
  const sawValueRef = useRef(false);
  const stoppedRef = useRef(false);
  const generationRef = useRef(0);

  const clearScheduled = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const tickRef = useRef<(generation: number) => Promise<void>>();

  const schedule = useCallback((delayMs: number, generation: number) => {
    clearScheduled();
    timeoutRef.current = setTimeout(() => {
      void tickRef.current?.(generation);
    }, delayMs);
  }, [clearScheduled]);

  const tick = useCallback(
    async (generation: number) => {
      if (stoppedRef.current || generation !== generationRef.current) {
        return;
      }

      setLoading(true);

      try {
        const result = await fetcherRef.current();

        if (stoppedRef.current || generation !== generationRef.current) {
          return;
        }

        consecutiveFailuresRef.current = 0;
        notFoundAttemptsRef.current = 0;
        if (result != null) {
          sawValueRef.current = true;
        }
        setError(null);
        setConsecutiveFailures(0);
        setData(result);
        setLoading(false);

        if (isTerminalRef.current(result)) {
          stoppedRef.current = true;
          return;
        }

        const baseInterval =
          typeof document !== 'undefined' && document.hidden ? HIDDEN_INTERVAL_MS : ACTIVE_INTERVAL_MS;
        schedule(baseInterval, generation);
      } catch (err) {
        if (stoppedRef.current || generation !== generationRef.current) {
          return;
        }

        const status = httpStatusOf(err);

        if (status === 404) {
          notFoundAttemptsRef.current += 1;
          const missing = sawValueRef.current || notFoundAttemptsRef.current >= NOT_FOUND_GRACE_ATTEMPTS;
          if (!missing) {
            // Still inside the grace window: `loading` stays true so the surface keeps its
            // skeleton rather than flashing a "could not be found" card and then a value.
            schedule(ACTIVE_INTERVAL_MS, generation);
            return;
          }
          setLoading(false);
          stoppedRef.current = true;
          setDeniedReason('not_found');
          return;
        }

        setLoading(false);

        if (status === 403) {
          stoppedRef.current = true;
          setDeniedReason('forbidden');
          return;
        }

        consecutiveFailuresRef.current += 1;
        setConsecutiveFailures(consecutiveFailuresRef.current);
        setError(err instanceof Error ? err : new Error(String(err)));

        const backoffMs = Math.min(ACTIVE_INTERVAL_MS * 2 ** consecutiveFailuresRef.current, BACKOFF_CAP_MS);
        schedule(backoffMs, generation);
      }
    },
    [schedule]
  );

  tickRef.current = tick;

  const refetch = useCallback(() => {
    stoppedRef.current = false;
    consecutiveFailuresRef.current = 0;
    notFoundAttemptsRef.current = 0;
    setError(null);
    setConsecutiveFailures(0);
    setDeniedReason(null);
    generationRef.current += 1;
    clearScheduled();
    void tick(generationRef.current);
  }, [clearScheduled, tick]);

  useEffect(() => {
    if (!active) {
      stoppedRef.current = true;
      clearScheduled();
      return;
    }

    stoppedRef.current = false;
    consecutiveFailuresRef.current = 0;
    notFoundAttemptsRef.current = 0;
    generationRef.current += 1;
    void tick(generationRef.current);

    // A poll scheduled while the tab was hidden is due up to 10 s out, so a reader coming back
    // to the tab would otherwise be looking at a value from before they left. Fetch on the
    // way in instead of waiting for the slow timer.
    const onVisibilityChange = () => {
      if (stoppedRef.current || document.hidden) return;
      clearScheduled();
      void tickRef.current?.(generationRef.current);
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }

    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
      stoppedRef.current = true;
      clearScheduled();
    };
    // Intentionally re-running only on `active` — `tick`/`clearScheduled` are stable
    // via refs and re-including them here would restart polling on every render.
  }, [active]);

  return { data, loading, error, consecutiveFailures, deniedReason, refetch };
}

export default usePolledResource;
